import { create } from 'zustand';
import { sameVehicle, type VehicleRef } from '../domain';
import type { SavedCarConfiguration } from './types';

/** Auto a cui una catena `parentId` appartiene: nuova o pronta consegna. */
export type ChainSubject =
	| { vehicleType: 'new'; vehicle: VehicleRef | null }
	| { vehicleType: 'stock'; stockId: string | null };

type RestoreState = {
	incoming: SavedCarConfiguration | null;
	/** Testa della catena `parentId`: cambia a ogni salvataggio. */
	restoredFrom: string | null;
	/** Auto a cui `restoredFrom` appartiene: la catena non attraversa auto diverse. */
	restoredFromSubject: ChainSubject | null;
	request: (record: SavedCarConfiguration) => void;
	setRestoredFrom: (id: string | null, subject: ChainSubject | null) => void;
	clear: () => void;
};

export const subjectOf = (record: SavedCarConfiguration): ChainSubject =>
	record.vehicleType === 'stock'
		? { vehicleType: 'stock', stockId: record.vehicleStockId }
		: {
				vehicleType: 'new',
				vehicle: record.payload?.progress?.vehicle ?? null,
		  };

const sameSubject = (a: ChainSubject | null, b: ChainSubject): boolean => {
	if (!a || a.vehicleType !== b.vehicleType) return false;
	return a.vehicleType === 'stock'
		? !!a.stockId && a.stockId === (b as { stockId: string | null }).stockId
		: sameVehicle(a.vehicle, (b as { vehicle: VehicleRef | null }).vehicle);
};

export const useRestoreStore = create<RestoreState>((set) => ({
	incoming: null,
	restoredFrom: null,
	restoredFromSubject: null,
	request: (record) =>
		set({
			incoming: record,
			restoredFrom: record.id,
			restoredFromSubject: subjectOf(record),
		}),
	setRestoredFrom: (restoredFrom, subject) =>
		set({ restoredFrom, restoredFromSubject: subject }),
	clear: () =>
		set({
			incoming: null,
			restoredFrom: null,
			restoredFromSubject: null,
		}),
}));

export const chainParentId = (
	restore: Pick<RestoreState, 'restoredFrom' | 'restoredFromSubject'>,
	subject: ChainSubject
): string | null =>
	restore.restoredFrom && sameSubject(restore.restoredFromSubject, subject)
		? restore.restoredFrom
		: null;

export const chainFrom = (
	restore: Pick<RestoreState, 'restoredFrom' | 'restoredFromSubject'>,
	subject: ChainSubject
): { parentId: string | null } => ({
	parentId: chainParentId(restore, subject),
});

export const stateSubject = (state: {
	vehicle: VehicleRef | null;
	stockVehicle: { id: string } | null;
}): ChainSubject =>
	state.stockVehicle
		? { vehicleType: 'stock', stockId: state.stockVehicle.id }
		: { vehicleType: 'new', vehicle: state.vehicle };

/** Il record consegnato può essere di un'altra auto: l'utente è tornato indietro. */
export const recordMatchesSubject = (
	record: SavedCarConfiguration | null | undefined,
	subject: ChainSubject
): record is SavedCarConfiguration =>
	!!record && sameSubject(subjectOf(record), subject);
